
<template>
  <div>
    <!-- common fields-->
    <div>
      <!-- Name -->
      <VTextField
        v-model="flowMeterName"
        :rules="{ required: true }"
        :label="$t('massBalance.simplified.flowMeterName')"
        outlined
        @input="onInput"
        @change="emitChange"
      />

      <!-- Display Meters -->
      <div class="d-flex">
        <VCheckbox
          v-model="displayMeters"
          :required="true"
          :label="$t('massBalance.simplified.displayMeters')"
          :showCustomLabel="false"
          outlined
          @input="onInput"
          @change="emitChange"
        />
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              color="grey"
              class="ml-1"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>
            {{ $t('massBalance.simplified.showMetersDescription') }}
          </span>
        </v-tooltip>
      </div>
      <v-radio-group
        v-model="flowMeterType"
        row
      >
        <v-radio
          v-for="(flowMeterType, index) in flowMeterTypes"
          :key="index"
          :label="flowMeterType.text"
          :value="flowMeterType.value"
          @change="changeFlowMeterType"
        />
      </v-radio-group>
    </div>
    <!-- flowmeter fields-->
    <div v-if="flowMeterType">
      <div
        v-for="(flowMeterData, index) in flowMetersData"
        :key="index"
      >
        <VSelect
          v-model="flowMeterData.networkId"
          :rules="{ required: true }"
          :label="$t('massBalance.modalStep.network')"
          :items="availableNetworks"
          :loading="networkLoading"
          outlined
          @input="onChangeNetwork(flowMeterData.networkId, true)"
          @change="emitChange"
        />

        <VAutocomplete
          v-model="flowMeterData.flowmeterIds"
          :rules="{ required: true }"
          :label="$t('massBalance.modalStep.flowmeter')"
          :items="flowMetersByNetwork[flowMeterData.networkId]"
          :loading="flowMeterLoading"
          outlined
          multiple
          @input="onInput"
          @change="emitChange"
        />

        <!-- delete Row-->
        <div
          v-if="flowMetersData.length > 1"
          class="my-1"
        >
          <v-col
            cols="12"
            class="d-flex flex-row"
          >
            <v-icon
              class="delete-circle-icon"
              @click="removeFlowMeterData(index)"
            >
              mdi-delete
            </v-icon>
            <span class="ml-2">{{ $t('forms.labels.delete') }}</span>
          </v-col>
          <v-divider
            dark
            style="height: 3px; background-color: gray;"
          />
        </div>
      </div>
      <!-- adding Row-->
      <div>
        <v-icon
          @click="addReportData"
        >
          add
        </v-icon>
        {{ $t('massBalance.add') }}
      </div>
    </div>
  </div>
</template>

<script>
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import { mapGetters, mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import { TYPE_BLOWDOWN, TYPE_MAKE_UP, TYPE_FEED } from '@/aquassist-shared/constants/types/flowMeters';

export default {
    components: {
        VTextField,
        VSelect,
        VCheckbox,
        VAutocomplete,
    },
    props: {
        massBalanceSelected: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            networks: [],
            networkId: null,
            flowMetersByNetwork: {},
            flowMeterName: '',
            displayMeters: false,
            networkLoading: false,
            flowMeterLoading: false,
            flowMeterType: null,
            flowMetersData: [{
                networkId: null,
                flowmeterIds: [],
            }],
        };
    },
    computed: {
        ...mapGetters([
            'site',
        ]),
        flowMeterTypes() {
            return [TYPE_MAKE_UP, TYPE_FEED, TYPE_BLOWDOWN].map(type => ({
                text: this.$t(`massBalance.modalStep.${type.toLowerCase()}`),
                value: type,
            }));
        },
        availableNetworks() {
            return this.networks.map(network => ({
                name: network.name,
                id: network.id,
                disabled: this.networkIsUsed(network.id),
            }));
        },
    },
    mounted() {
        this.onInput();
        this.emitChange();
    },
    created() {
        this.getNetworks();
        this.modeEdit = !isEmpty(this.massBalanceSelected);
        if (this.modeEdit) {
            this.flowMeterName = this.massBalanceSelected.flowmeterGeneralProps.name;
            this.displayMeters = this.massBalanceSelected.flowmeterGeneralProps.displayMeters;
            // we always has just one type of flowmeter for each simplified mass balance so the flowmeters array has just one element
            this.flowMetersData = this.massBalanceSelected.flowmeters.length > 0 ? this.massBalanceSelected.flowmeters[0].config : [];
            this.flowMeterType = this.massBalanceSelected.flowmeters.length > 0 ? this.massBalanceSelected.flowmeters[0].type : null;
            this.flowMetersData.forEach(flowMeterData => {
                this.onChangeNetwork(flowMeterData.networkId);
            });
        }
    },
    methods: {
        ...mapActions({
            setFlowmeterNetwork: 'setFlowmeterNetwork',
        }),
        async getNetworks() {
            this.networkLoading = true;
            const { data } = await this.$rq.getAllNetworks(this.site.id);
            this.networks = data;
            this.networkLoading = false;
            this.onInput();
        },
        networkIsUsed(networkId) {
            return this.flowMetersData.some(flowMeterData => flowMeterData.networkId === networkId);
        },
        async onChangeNetwork(networkId, resetFlowMeterIds = false) {
            this.flowMeterLoading = true;
            if (resetFlowMeterIds) {
                const fm = this.flowMetersData.find(flowMeterData => flowMeterData.networkId === networkId);
                fm.flowmeterIds = [];
            }
            const { data } = await this.$rq.getMassBalanceFlowMeterByNetwork(this.site.id, networkId, this.flowMeterType);
            this.flowMetersByNetwork = {
                ...this.flowMetersByNetwork,
                [networkId]: data,
            };
            this.flowMeterLoading = false;
        },
        async removeFlowMeterData(index) {
            this.flowMetersData.splice(index, 1);
            this.emitChange();
        },
        onInput() {
            const data = [{
                type: this.flowMeterType,
                config: this.flowMetersData,
            }];
            const summaryData = [{
                config: this.flowMetersData?.map(fd => ({
                    network: this.networks?.find(network => network.id === fd.networkId),
                    flowmeters: fd.flowmeterIds?.map(id => this.flowMetersByNetwork[fd.networkId]
                        ?.find(flowMeter => flowMeter.id === id)),

                })),
                type: this.flowMeterType,
            }];
            const flowmeterGeneralProps = {
                name: this.flowMeterName,
                displayMeters: this.displayMeters,
            };

            this.setFlowmeterNetwork({
                flowMeters: { data, summaryData, allNetworks: this.networks, selectedNetworks: this.networks, flowmeterGeneralProps },
            });
        },
        changeFlowMeterType() {
            this.flowMetersData = [{
                networkId: null,
                flowmeterIds: [],
            }];
            this.flowMetersByNetwork = {};
        },
        addReportData() {
            this.flowMetersData = [
                ...this.flowMetersData,
                {
                    networkId: '',
                    flowmeterIds: [],
                },
            ];
            this.emitChange();
        },
        validateFlowMeters() {
            const validFlowMeters = this.flowMetersData.every(flowMeterData => !!flowMeterData.networkId && flowMeterData.flowmeterIds.length > 0);
            return validFlowMeters && !!this.flowMeterType && !!this.flowMeterName;
        },
        emitChange() {
            this.$emit('inputChange', this.validateFlowMeters);
        },
    },
};
</script>
<style scoped>
.delete-circle-icon {
  background-color: #b71c1c;
  color: #fff !important;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: background 0.2s;
}
.delete-circle-icon:hover {
  background-color: #b71c1c;
}

</style>
